import React, { useState } from "react";
import { NormalizedFile } from "../../utils/fileNormalization";
import {
  KNOWLEDGE_FILE_ACCEPT,
  KNOWLEDGE_FILE_LABEL,
  knowledgeFileDuplicateMessage,
  knowledgeFileRejectionMessage,
  partitionDuplicateKnowledgeFiles,
  partitionKnowledgeFiles,
} from "../../utils/knowledgeFileTypes";
import { useToast } from "../../../../context/ToastContext";

interface KnowledgeFilesSectionProps {
  knowledgeFiles: NormalizedFile[];
  newKnowledgeFiles: File[];
  savingKnowledgeFiles: boolean;
  disabled?: boolean;
  onRemoveFile: (key: string) => void;
  onRemoveNewFile: (index: number) => void;
  onAddFiles: (files: File[]) => void;
  onSave: () => void;
  hasChanges: boolean;
}

/**
 * Whether the agent can actually answer from this file.
 *
 * Attaching a file and indexing it are separate steps — embedding runs asynchronously
 * after the upload returns — and a file that produced no text (a scanned PDF is the
 * usual case) used to look identical to one that worked. "unknown" renders nothing:
 * those files predate the backend recording status, and a warning badge on them would
 * be a guess.
 */
const STATUS_BADGES: Record<
  string,
  { label: string; bg: string; text: string; border: string; hint: string }
> = {
  indexed: {
    label: "Indexed",
    bg: "bg-green-50",
    text: "text-green-700",
    border: "border-green-200",
    hint: "This file is part of the agent's knowledge.",
  },
  processing: {
    label: "Processing",
    bg: "bg-gray-50",
    text: "text-gray-600",
    border: "border-gray-200",
    hint: "Being indexed — this can take a few minutes for large files.",
  },
  failed: {
    label: "Not readable",
    bg: "bg-red-50",
    text: "text-red-700",
    border: "border-red-200",
    hint: "The agent cannot answer from this file.",
  },
};

const KnowledgeFilesSection: React.FC<KnowledgeFilesSectionProps> = ({
  knowledgeFiles,
  newKnowledgeFiles,
  savingKnowledgeFiles,
  disabled = false,
  onRemoveFile,
  onRemoveNewFile,
  onAddFiles,
  onSave,
  hasChanges,
}) => {
  const [rejectionMessage, setRejectionMessage] = useState<string | null>(null);
  const { showToast } = useToast();

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const incoming = Array.from(e.target.files || []);
    e.target.value = ""; // Reset input
    if (incoming.length === 0) return;

    // The picker's accept filter is bypassable ("All files"), and the presign lambda
    // 400s on an unsupported extension — catch it here so the user sees which file was
    // the problem instead of a generic upload failure part-way through the batch.
    const { accepted, rejectedNames } = partitionKnowledgeFiles(incoming);
    setRejectionMessage(knowledgeFileRejectionMessage(rejectedNames));

    // Re-uploading a file already attached silently created a second copy: every upload
    // gets a fresh timestamped name, so nothing collided and nothing complained. The
    // agent then embedded the same content twice, which wastes storage and lets one
    // document dominate retrieval. Check against attached AND already-staged files.
    const { fresh, duplicateNames } = partitionDuplicateKnowledgeFiles(accepted, [
      ...knowledgeFiles.map((f) => f.filename),
      ...newKnowledgeFiles.map((f) => f.name),
    ]);
    const duplicateMessage = knowledgeFileDuplicateMessage(duplicateNames);
    if (duplicateMessage) showToast(duplicateMessage, "warning");

    if (fresh.length > 0) onAddFiles(fresh);
  };

  return (
    <div className="mb-6">
      <label className="block text-gray-700 font-medium mb-2">
        Knowledge Files (Optional)
      </label>
      <p className="text-sm text-gray-600 mb-3">
        Upload files to expand this agent's knowledge. You can also remove
        previously attached files.
      </p>

      {/* Existing attached files */}
      <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm font-medium text-gray-700">
            Attached files ({knowledgeFiles.length})
          </p>
        </div>

        {knowledgeFiles.length === 0 ? (
          <p className="text-sm text-gray-500 italic">
            No knowledge files attached.
          </p>
        ) : (
          <ul className="space-y-2">
            {knowledgeFiles.map((f) => {
              const typeBadge =
                f.type === "web_crawl"
                  ? { label: "Website", bg: "sgl-badge-outline", text: "", border: "" }
                  : f.type === "default"
                  ? { label: "Guide", bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-200" }
                  : f.type === "assistant"
                  ? { label: "Written by assistant", bg: "sgl-badge-gold", text: "", border: "" }
                  : f.type === "user"
                  ? { label: "Uploaded", bg: "sgl-tint-sage", text: "", border: "" }
                  : null;
              const canRemove = f.type !== "default";
              const statusBadge = f.status ? STATUS_BADGES[f.status] : undefined;
              return (
                <li
                  key={`${f.bucket}:${f.key}`}
                  className="flex items-center justify-between bg-white border border-gray-200 rounded-md px-3 py-2"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      {typeBadge && (
                        <span className={`shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-semibold ${typeBadge.bg} ${typeBadge.text} ${typeBadge.border}`}>
                          {typeBadge.label}
                        </span>
                      )}
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {f.filename}
                      </p>
                      {statusBadge && (
                        <span
                          title={statusBadge.hint}
                          className={`shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-semibold ${statusBadge.bg} ${statusBadge.text} ${statusBadge.border}`}
                        >
                          {statusBadge.label}
                        </span>
                      )}
                    </div>
                    {f.status === "failed" && (
                      <p className="mt-1 text-xs text-red-600">
                        {f.statusDetail ||
                          "The agent cannot answer from this file."}
                      </p>
                    )}
                  </div>
                  {canRemove && (
                    <button
                      type="button"
                      onClick={() => onRemoveFile(f.key)}
                      className="ml-3 text-red-600 hover:text-red-700 text-sm whitespace-nowrap"
                      disabled={disabled || savingKnowledgeFiles}
                    >
                      Remove
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Add new files */}
      <div className="mt-4">
        <input
          type="file"
          multiple
          onChange={handleFileSelect}
          className="hidden"
          id="knowledge-files-input"
          disabled={disabled || savingKnowledgeFiles}
          accept={KNOWLEDGE_FILE_ACCEPT}
        />
        <label
          htmlFor="knowledge-files-input"
          className={`inline-flex items-center px-4 py-2 bg-white border border-gray-300 rounded cursor-pointer hover:bg-gray-50 transition-colors text-gray-700 ${
            disabled || savingKnowledgeFiles
              ? "opacity-50 cursor-not-allowed"
              : ""
          }`}
        >
          + Add files
        </label>
        <p className="mt-2 text-xs text-gray-500">
          Supported formats: {KNOWLEDGE_FILE_LABEL}. These are the formats we can
          read text from and index for the agent's long-term recall — chat
          attachments accept a wider range because the model reads those directly.
        </p>
        {rejectionMessage && (
          <p className="mt-1 text-xs text-red-600">{rejectionMessage}</p>
        )}

        {newKnowledgeFiles.length > 0 && (
          <div className="mt-3">
            <p className="text-sm font-medium text-gray-700 mb-2">
              New files to upload ({newKnowledgeFiles.length})
            </p>
            <ul className="space-y-2">
              {newKnowledgeFiles.map((file, idx) => (
                <li
                  key={`${file.name}-${file.size}-${idx}`}
                  className="flex items-center justify-between bg-white border border-gray-200 rounded-md px-3 py-2"
                >
                  <p className="text-sm text-gray-900 truncate flex-1">
                    {file.name}
                  </p>
                  <button
                    type="button"
                    onClick={() => onRemoveNewFile(idx)}
                    className="ml-3 text-red-600 hover:text-red-700 text-sm whitespace-nowrap"
                    disabled={disabled || savingKnowledgeFiles}
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Save button */}
      <div className="mt-4">
        <button
          type="button"
          onClick={onSave}
          disabled={disabled || savingKnowledgeFiles || !hasChanges}
          className={`w-full py-2 px-4 rounded-lg text-white ${
            savingKnowledgeFiles || !hasChanges
              ? "bg-brand-primary opacity-50 cursor-not-allowed"
              : "bg-brand-primary hover:bg-brand-primary-hover"
          }`}
        >
          {savingKnowledgeFiles ? "Saving..." : "Save Knowledge Files"}
        </button>
        <p className="text-xs text-gray-500 mt-2">
          This only updates attached knowledge files (add/remove). Use "Update
          Agent Instructions" to save instructions/tools/profile.
        </p>
      </div>
    </div>
  );
};

export default KnowledgeFilesSection;
